import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, GraduationCap, X, Download, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClassManagement() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', section: 'A', class_teacher_id: '', academic_year: '2025-2026', capacity: 40 });
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`, { headers });
      setClasses(res.data.data || []);
    } catch { toast.error('Failed to load classes'); }
    setLoading(false);
  };

  const fetchTeachers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers });
      setTeachers((res.data.data?.users || []).filter(u => u.role === 'teacher' || u.role === 'hr'));
    } catch {}
  };

  useEffect(() => { fetchClasses(); fetchTeachers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/classes/${editId}`, form, { headers });
        toast.success('Class updated');
      } else {
        await axios.post(`${API}/classes`, form, { headers });
        toast.success('Class created');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ name: '', section: 'A', class_teacher_id: '', academic_year: '2025-2026', capacity: 40 });
      fetchClasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleEdit = (cls) => {
    setForm({ name: cls.name, section: cls.section, class_teacher_id: cls.class_teacher_id || '', academic_year: cls.academic_year, capacity: cls.capacity });
    setEditId(cls.class_id);
    setShowForm(true);
  };

  const handleDelete = (cls) => {
    setConfirm({
      title: 'Delete Class',
      message: `Delete ${cls.name} ${cls.section}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/classes/${cls.class_id}`, { headers });
          toast.success('Class deleted');
          fetchClasses();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
      }
    });
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/classes/bulk-upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setCsvResult(res.data.data);
      toast.success(`Created ${res.data.data.created} classes`);
      fetchClasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/classes/bulk-template`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'classes_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const CLASS_NAMES = ['Nursery', 'LKG', 'UKG', ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`)];
  const SECTIONS = ['A', 'B', 'C', 'D', 'E'];

  return (
    <div className="space-y-6" data-testid="class-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="class-management-title">Class Management</h1>
          <p className="text-sm text-slate-500 mt-1">{classes.length} classes configured</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-200 text-slate-600" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleCsvUpload} />
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4" /> {csvUploading ? 'Uploading...' : 'Import CSV'}
            </span>
          </label>
          <Button onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', section: 'A', class_teacher_id: '', academic_year: '2025-2026', capacity: 40 }); }} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="add-class-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Class
          </Button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="class-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editId ? 'Edit Class' : 'Add New Class'}</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Class Name *</Label>
                  <Select value={form.name} onValueChange={(v) => setForm({ ...form, name: v })}>
                    <SelectTrigger data-testid="class-name-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{CLASS_NAMES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Section *</Label>
                  <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                    <SelectTrigger data-testid="class-section-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Class Teacher</Label>
                <Select value={form.class_teacher_id} onValueChange={(v) => setForm({ ...form, class_teacher_id: v })}>
                  <SelectTrigger data-testid="class-teacher-select"><SelectValue placeholder="Assign teacher" /></SelectTrigger>
                  <SelectContent>{teachers.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Academic Year</Label>
                  <Input value={form.academic_year} onChange={(e) => setForm({ ...form, academic_year: e.target.value })} data-testid="class-year-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Capacity</Label>
                  <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} data-testid="class-capacity-input" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="class-save-btn">{editId ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Class Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : classes.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-testid="no-classes">
          <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No classes configured yet</p>
          <p className="text-sm text-slate-400 mt-1">Add classes to start enrolling students</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="classes-grid">
          {classes.map(cls => (
            <div key={cls.class_id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`class-card-${cls.class_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{cls.name}</h3>
                    <span className="text-xs text-slate-400">Section {cls.section}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(cls)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`edit-class-${cls.class_id}`}><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                  <button onClick={() => handleDelete(cls)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-class-${cls.class_id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {cls.student_count || 0}/{cls.capacity}</span>
                {cls.class_teacher_name && <span className="truncate max-w-[120px]">{cls.class_teacher_name}</span>}
              </div>
              <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
                <div className="bg-teal-400 h-1.5 rounded-full" style={{ width: `${Math.min(((cls.student_count || 0) / cls.capacity) * 100, 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSV Result Dialog */}
      {csvResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold mb-4">Import Results</h2>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{csvResult.created}</p>
                <p className="text-xs text-emerald-600">Created</p>
              </div>
              <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{csvResult.skipped}</p>
                <p className="text-xs text-amber-600">Skipped</p>
              </div>
            </div>
            {csvResult.errors?.length > 0 && (
              <div className="max-h-36 overflow-y-auto space-y-1 mb-4">
                {csvResult.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-500">Row {e.row}: {e.error}</p>
                ))}
              </div>
            )}
            <Button className="w-full bg-teal-500 text-white rounded-xl" onClick={() => setCsvResult(null)}>Done</Button>
          </div>
        </div>
      )}

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
